import React, { useState, useRef, useEffect, useCallback } from "react"
import { Settings, LogOut, ChevronDown } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import { useNavigate } from "react-router-dom"
import toast from "react-hot-toast"

import { useAuthStore } from "@store"

const UserProfileDropdown: React.FC = () => {
  const { user, logout } = useAuthStore()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleLogout = async () => {
    try {
      await logout()
      toast.success("Logged out successfully!")
      setIsOpen(false)
    } catch (error: any) {
      toast.error(error.msg || "Error logging out. Please try again.")
    }
  }

  const onCloseDropdown = useCallback((callback?: () => void) => {
    setIsOpen(false)
    callback?.()
  }, [])

  return (
    <>
      <div className="relative ms-4" ref={dropdownRef}>
        {/* User Avatar Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          // rounded-lg hover:bg-legacy-light/20 transition-colors duration-200
          className="flex items-center space-x-2 px-1"
        >
          {/* {user?.avatar ? (
            <img src={user.avatar} alt={`${user.firstName} ${user.lastName}`} className="h-8 w-8 rounded-full object-cover border-2 border-legacy-light dark:border-legacy-light/30" />
          ) : (
            <div className="h-8 w-8 bg-legacy-blue rounded-full flex items-center justify-center border-2 border-legacy-light dark:border-legacy-light/30">
              <User className="h-4 w-4 text-white" />
            </div>
          )} */}
          <div className="text-left">
            <p className="text-sm font-medium text-legacy-dark dark:text-legacy-cream">{user?.name}</p>
            <p className="text-xs hidden sm:block text-legacy-light dark:text-legacy-light/70">
              {user?.email}
            </p>
          </div>
          <ChevronDown
            className={`h-4 w-4 text-legacy-dark dark:text-legacy-cream transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          />
        </button>

        {/* Dropdown Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="absolute right-0 mt-2 w-64 bg-white dark:bg-legacy-navy rounded-lg shadow-lg border border-legacy-light/20 dark:border-legacy-light/10 py-2 z-50 max-h-96 overflow-y-auto"
            >
              <button
                onClick={() => onCloseDropdown(() => navigate("/profile"))}
                className="w-full px-4 py-2 text-left text-sm text-legacy-dark dark:text-legacy-cream hover:bg-legacy-light/20 dark:hover:bg-legacy-light/10 flex items-center space-x-2"
              >
                <Settings className="h-4 w-4" />
                <span>Update Profile</span>
              </button>

              <hr className="my-2 border-legacy-light/20 dark:border-legacy-light/10" />

              <button
                onClick={handleLogout}
                className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center space-x-2"
              >
                <LogOut className="h-4 w-4" />
                <span>Sign Out</span>
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  )
}

export default UserProfileDropdown
